

































/* eslint-disable max-lines-per-function */
import {
  computed,
  defineComponent,
  onMounted,
  useAsync,
  useContext,
  useRoute,
  useRouter,
  watch,
} from '@nuxtjs/composition-api';
import AppFooter from '@shared/components/AppFooter/AppFooter.vue';
import useCartCookie from '@shared/composables/useCartCookie';
import useFeatureFlag from '@shared/composables/useFeatureFlag';
import useLocalStorageCart from '@shared/composables/useLocalStorageCart';
import useMyGarage from '@shared/composables/useMyGarage';
import usePopularBrands from '@shared/composables/usePopularBrands';
import useSbCategories from '@shared/composables/useSbCategories';
import useSearchInsights from '@shared/composables/useSearchInsights';
import useShopifyDiscounts from '@shared/composables/useShopifyDiscounts';
import useUiState from '@shared/composables/useUiState';
import useWebsiteBanner from '@shared/composables/useWebsiteBanner';
import { ASKUS_LIST_VAR } from '@shared/helpers/variables/search-io';
import { StoryblokWebsiteBanner } from '@shared/ui';
import { searchGetters, useSearch } from '@unified-commerce/gpc-vue-storefront-search';
import { useUser } from '@unified-commerce/gpc-vue-storefront-shopify';
import { getStoryBlokVersion, useContent } from '@unified-commerce/gpc-vue-storefront-storyblok';

import RemoveAddressModal from '~/components/Account/RemoveAddressModal.vue';
import AccountModal from '~/components/AccountModal/AccountModal.vue';
import AppHeader from '~/components/AppHeader/AppHeader.vue';
import BikeFinderModal from '~/components/BikeFinderModal/BikeFinderModal.vue';
import CartSidebar from '~/components/CartSidebar/CartSidebar.vue';
import EmailSubscription from '~/components/EmailSubscription.vue';
import LogoutModal from '~/components/LogoutModal.vue';
import Notification from '~/components/Notification.vue';
import RecentlyAddedSidebar from '~/components/RecentlyAddedSidebar/RecentlyAddedSidebar.vue';
import StoreLocatorBanner from '~/components/Stores/StoreLocatorBanner.vue';
import StoresSidebar from '~/components/Stores/StoresSidebar.vue';
import VehiclesSidebar from '~/components/VehiclesSideBar/VehiclesSidebar.vue';
import { useSearchPreFilters } from '~/composables/useSearchPreFilters';

export default defineComponent({
  name: 'DefaultLayout',
  components: {
    VehiclesSidebar,
    BikeFinderModal,
    RemoveAddressModal,
    StoreLocatorBanner,
    StoresSidebar,
    StoryblokWebsiteBanner,
    AppHeader,
    CartSidebar,
    RecentlyAddedSidebar,
    AppFooter,
    AccountModal,
    LogoutModal,
    Notification,
    EmailSubscription,
  },
  setup() {
    const { $config } = useContext();
    const { buildContextualSearchQuery, clearFilters: clearPreFilters } = useSearchPreFilters();
    const { isAccountModalOpen, isLogoutModalOpen, closeLogoutModal, isSignup } = useUiState();
    const route = useRoute();
    const router = useRouter();
    const { load: loadUser, user } = useUser();
    const { load: loadCart, cart } = useCartCookie();
    const { featureFlagEnabled } = useFeatureFlag();
    const { search: headerNavigationSearch, content: headerNavigationMenuContent } =
      useContent('headerNavigationMenu');
    const { selectedVehicle, updateVehicle } = useMyGarage();
    const { initSearchInsights } = useSearchInsights();

    const { load: loadDiscounts } = useShopifyDiscounts();
    const { fetchWebsiteBanner, websiteBannerSlides, hasWebsiteBanner } = useWebsiteBanner();

    const { search: searchSbCategories, sbCategories } = useSbCategories();

    const { search, result: searchResult, loading: searchResultLoading } = useSearch('');

    const headerNavigationMenu = computed(() => headerNavigationMenuContent.value);

    const { brands, fetchPopularBrands } = usePopularBrands();

    const { setCartLocalStorage } = useLocalStorageCart();

    const searchBarSearch = async (input: string) => {
      await search({
        query: input,
        page: 0,
        facets: ['*'],
        hitsPerPage: 3,
        filters: `price > 0`,
        enableAutocomplete: true,
      });

      await fetchPopularBrands({ searchResult: searchResult.value, query: input });
    };

    const searchBarEnter = (input: string) => {
      // Merges any committed pre-filter context (selected tab + facets); falls back to a plain
      // /search link when no context has been set.
      router.push(buildContextualSearchQuery(input));
      // URL already carries the filters; reset the in-memory UI state (modal + badge).
      clearPreFilters();
    };

    useAsync(async () => {
      const version = getStoryBlokVersion($config.ENV, route.value);
      await Promise.allSettled([
        headerNavigationSearch({
          url: 'navigation-menus/header',
          version,
          relations: 'ArticleSelector.article',
        }),
        fetchWebsiteBanner(),
        searchSbCategories(),
      ]);
    });

    onMounted(async () => {
      window.zESettings = {
        webWidget: {
          color: { theme: '#bd0f16' },
        },
      };

      initSearchInsights();
      if (featureFlagEnabled) await loadDiscounts();
      await loadCart();
      await loadUser();

      setCartLocalStorage();
    });

    const sortedASKUs = computed(() => {
      const ASKUs_list = searchGetters.getPipelineVariable(searchResult.value, ASKUS_LIST_VAR);
      const ASKUS_array = ASKUs_list ? ASKUs_list.split(',') : [];
      return ASKUS_array.sort((a: string, b: string) => a.localeCompare(b));
    });

    watch(
      () => selectedVehicle.value?.vehicleId,
      () => {
        if (selectedVehicle.value) updateVehicle(selectedVehicle.value);
      },
      { immediate: true },
    );

    return {
      route,
      headerNavigationMenu,
      websiteBannerSlides,
      cart,
      isAccountModalOpen,
      isSignup,
      isLogoutModalOpen,
      hasWebsiteBanner,
      closeLogoutModal,
      searchBarSearch,
      searchResult,
      searchResultLoading,
      searchBarEnter,
      brands,
      sortedASKUs,
      user,
      sbCategories,
    };
  },
  head() {
    return {
      link: [
        {
          hid: 'canonical-url',
          rel: 'canonical',
          href: `https://www.amxsuperstores.com.au${this.$route.path.replace(/\/$/, '')}`,
        },
      ],
    };
  },
});
